import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { initialState } from "./initialState";
import {
  getBulkAnalyzerUploadedFiles,
  getBulkAnalyzerUploadsStats,
} from "./bulkAnalyzerFiles.thunks";
import { TUploadedFile } from "../../services/apiV2.types";
import { enqueueSnackbar } from "notistack";
import { LOAD_STATS_FAILURE } from "../../constants";
import { TBulkAnalyzerFilesState } from "./bulkAnalyzerFiles.types";

const bulkAnalyzerFilesSlice = createSlice({
  name: "bulkAnalyzerFiles",
  initialState,
  selectors: {
    selectFileById: (state, id: TUploadedFile["productUploadId"]) => {
      return state.files.find((file) => file.productUploadId === id);
    },
  },
  reducers: {
    updateFileName: (
      state,
      action: PayloadAction<{ productUploadId: string; fileName: string }>,
    ) => {
      const index = state.files.findIndex(
        (file) => file.productUploadId === action.payload.productUploadId,
      );
      state.files[index].fileName = action.payload.fileName;
    },
    setFiles: (state, action: PayloadAction<TUploadedFile[]>) => {
      state.files = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<TBulkAnalyzerFilesState["paginationModel"]>,
    ) => {
      state.paginationModel = action.payload;
    },
    incrementStats: (state) => {
      const { filesUploaded } = state.stats;
      state.stats = {
        ...state.stats,
        filesUploaded: filesUploaded + 1,
      };
    },
    decrementStats: (state) => {
      const { filesUploaded } = state.stats;
      state.stats = {
        ...state.stats,
        filesUploaded: filesUploaded - 1,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBulkAnalyzerUploadedFiles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBulkAnalyzerUploadedFiles.fulfilled, (state, action) => {
        const { data, total } = action.payload;
        state.isLoading = false;
        if (action.meta.arg.forceRefetch) {
          state.files = [...data];
        } else {
          state.files = [...state.files, ...data];
        }
        state.total = total;
        // state.isPolling = data.some((file) => !file.processedPercentage);
      })
      .addCase(getBulkAnalyzerUploadedFiles.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(getBulkAnalyzerUploadsStats.pending, (state) => {
        state.stats.isLoading = true;
      })
      .addCase(getBulkAnalyzerUploadsStats.fulfilled, (state, action) => {
        const { fileUploaded, itemScanned, planName } = action.payload;
        state.stats = {
          filesScanned: itemScanned,
          filesUploaded: fileUploaded,
          membership: planName,
          isLoading: false,
        };
      })
      .addCase(getBulkAnalyzerUploadsStats.rejected, (state) => {
        state.stats.isLoading = false;
        enqueueSnackbar(LOAD_STATS_FAILURE, { variant: "error" });
      });
  },
});

export const { selectFileById } = bulkAnalyzerFilesSlice.getSelectors();
export const {
  updateFileName,
  setFiles,
  setPagination,
  incrementStats,
  decrementStats,
} = bulkAnalyzerFilesSlice.actions;
export default bulkAnalyzerFilesSlice.reducer;
